import { Box, Card, CardContent, Typography } from '@mui/material';
import {
  formatPercent,
  getAccuracyColor,
  getSignalColor,
  TYPOGRAPHY,
} from '../../theme/forecastStyles';
import type { StockHistoryDecayResponse } from '../../lib/api/stock-history';

interface DecayAnalysisProps {
  decay: StockHistoryDecayResponse;
}

export default function DecayAnalysis({ decay }: DecayAnalysisProps) {
  return (
    <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Signal Decay (1d / 3d / 7d)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              How forecast accuracy evolves as holding windows extend.
            </Typography>
          </Box>
        </Box>

        {decay.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {decay.map((item) => (
              <Box key={item.signal}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: TYPOGRAPHY.tiny,
                      fontWeight: 500,
                      color: getSignalColor(item.signal),
                    }}
                  >
                    {item.signal}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
                    {item.sampleSize} samples
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                  {item.decay.map((d) => (
                    <Box key={d.horizon}>
                      <Box
                        sx={{
                          bgcolor: 'grey.900',
                          border: 1,
                          borderColor: 'grey.800',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>
                          {d.horizon}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: getAccuracyColor(d.accuracy),
                          }}
                        >
                          {formatPercent(d.accuracy)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" sx={{ fontSize: TYPOGRAPHY.tiny, color: 'text.secondary' }}>
            Decay analytics will populate once enough completed 1d/3d/7d windows are available.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
